import {
  useState,
  type ReactNode,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  CircleHelp,
  TriangleAlert,
} from 'lucide-react'

import { Button } from './button'
import { Dialog } from './dialog'

export type AlertDialogVariant =
  | 'confirmation'
  | 'warning'
  | 'destructive'

export interface AlertDialogProps {
  isOpen: boolean

  onOpenChange: (
    isOpen: boolean,
  ) => void

  title: ReactNode
  description: ReactNode

  variant?: AlertDialogVariant

  /**
   * Optional overrides. When omitted, translated defaults
   * from the locale file are used.
   */
  confirmLabel?: string
  cancelLabel?: string
  processingLabel?: string
  instruction?: ReactNode

  isConfirmDisabled?: boolean

  onConfirm:
    () => void | Promise<void>

  onConfirmError?: (
    error: unknown,
  ) => void
}

export function AlertDialog({
  isOpen,
  onOpenChange,
  title,
  description,
  variant = 'confirmation',
  confirmLabel,
  cancelLabel,
  processingLabel,
  instruction,
  isConfirmDisabled = false,
  onConfirm,
  onConfirmError,
}: AlertDialogProps) {
  const { t } = useTranslation()

  const [
    isConfirming,
    setIsConfirming,
  ] = useState(false)

  const isDestructive =
    variant === 'destructive'

  const isWarning =
    variant === 'warning'

  const resolvedConfirmLabel =
    confirmLabel ??
    t('actions.confirm')

  const resolvedCancelLabel =
    cancelLabel ??
    t('actions.cancel')

  const resolvedProcessingLabel =
    processingLabel ??
    t('ui.loading.processing')

  const resolvedInstruction =
    instruction ??
    t('ui.alertDialog.instruction')

  async function handleConfirm():
    Promise<void> {
    if (
      isConfirming ||
      isConfirmDisabled
    ) {
      return
    }

    setIsConfirming(true)

    try {
      await onConfirm()
      onOpenChange(false)
    } catch (error: unknown) {
      onConfirmError?.(error)
    } finally {
      setIsConfirming(false)
    }
  }

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      role="alertdialog"
      size="small"
      isDismissable={false}
      showCloseButton={false}
      footer={({ close }) => (
        <>
          <Button
            variant="secondary"
            isDisabled={isConfirming}
            onPress={close}
          >
            {resolvedCancelLabel}
          </Button>

          <Button
            variant={
              isDestructive
                ? 'danger'
                : 'primary'
            }
            isDisabled={
              isConfirmDisabled ||
              isConfirming
            }
            onPress={() => {
              void handleConfirm()
            }}
          >
            {isConfirming
              ? resolvedProcessingLabel
              : resolvedConfirmLabel}
          </Button>
        </>
      )}
    >
      <div className="flex items-start gap-4">
        <div
          className={[
            'flex h-11 w-11 shrink-0',
            'items-center justify-center',
            'rounded-full',
            isDestructive
              ? [
                  'bg-(--color-danger-muted)',
                  'text-(--color-danger)',
                ].join(' ')
              : isWarning
                ? [
                    'bg-(--color-warning-muted)',
                    'text-(--color-warning)',
                  ].join(' ')
                : [
                    'bg-(--color-primary-muted)',
                    'text-(--color-primary)',
                  ].join(' '),
          ].join(' ')}
        >
          {variant === 'confirmation' ? (
            <CircleHelp
              aria-hidden="true"
              className="h-6 w-6"
            />
          ) : (
            <TriangleAlert
              aria-hidden="true"
              className="h-6 w-6"
            />
          )}
        </div>

        <p className="text-sm leading-6 text-(--color-muted-foreground)">
          {resolvedInstruction}
        </p>
      </div>
    </Dialog>
  )
}